<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Checklists') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Checklists allow you to break down tasks into smaller, trackable items. This helps ensure all requirements are met and provides a clear view of task completion progress.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  "ProjectSettings|When enabled, you can add checklists to tasks with multiple items. Each item can be checked off as it's completed, providing a visual indicator of progress. Checklists help ensure nothing is forgotten and make complex tasks more manageable."
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Use Cases') }}</h4>

            <div style="margin-bottom: 20px">
              <strong style="color: #333; display: block; margin-bottom: 8px">{{
                s__('ProjectSettings|Example 1: Deployment Checklist')
              }}</strong>
              <div class="checklist-example">
                <div class="checklist-example-header">
                  <h5 class="checklist-example-title">
                    {{ s__('ProjectSettings|Deployment Checklist') }}
                  </h5>
                  <div class="checklist-example-progress">
                    <div class="checklist-example-progress-bar">
                      <div class="checklist-example-progress-fill" style="width: 50%"></div>
                    </div>
                    <span class="checklist-example-progress-text"
                      >3/6 {{ s__('Checklist|completed') }}</span
                    >
                  </div>
                </div>
                <ul class="checklist-example-items">
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Run tests')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Update documentation')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Deploy to staging')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Verify staging')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Deploy to production')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Monitor logs')
                    }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div style="margin-bottom: 20px">
              <strong style="color: #333; display: block; margin-bottom: 8px">{{
                s__('ProjectSettings|Example 2: Code Review Checklist')
              }}</strong>
              <div class="checklist-example">
                <div class="checklist-example-header">
                  <h5 class="checklist-example-title">
                    {{ s__('ProjectSettings|Code Review Checklist') }}
                  </h5>
                  <div class="checklist-example-progress">
                    <div class="checklist-example-progress-bar">
                      <div class="checklist-example-progress-fill" style="width: 100%"></div>
                    </div>
                    <span class="checklist-example-progress-text"
                      >5/5 {{ s__('Checklist|completed') }}</span
                    >
                  </div>
                </div>
                <ul class="checklist-example-items">
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Code follows style guide')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Tests are included')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Documentation is updated')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|No security vulnerabilities')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Performance is acceptable')
                    }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div style="margin-bottom: 20px">
              <strong style="color: #333; display: block; margin-bottom: 8px">{{
                s__('ProjectSettings|Example 3: Feature Launch Checklist')
              }}</strong>
              <div class="checklist-example">
                <div class="checklist-example-header">
                  <h5 class="checklist-example-title">
                    {{ s__('ProjectSettings|Feature Launch Checklist') }}
                  </h5>
                  <div class="checklist-example-progress">
                    <div class="checklist-example-progress-bar">
                      <div class="checklist-example-progress-fill" style="width: 33%"></div>
                    </div>
                    <span class="checklist-example-progress-text"
                      >2/6 {{ s__('Checklist|completed') }}</span
                    >
                  </div>
                </div>
                <ul class="checklist-example-items">
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Design approved')
                    }}</span>
                  </li>
                  <li class="checklist-example-item completed">
                    <input type="checkbox" checked disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Development complete')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|QA testing passed')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|User documentation ready')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Marketing materials prepared')
                    }}</span>
                  </li>
                  <li class="checklist-example-item">
                    <input type="checkbox" disabled class="checklist-example-checkbox" />
                    <span class="checklist-example-item-text">{{
                      s__('ProjectSettings|Launch announcement scheduled')
                    }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <h4>{{ s__('ProjectSettings|Benefits') }}</h4>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Break down complex tasks into manageable steps') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Track progress visually with checkboxes') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Ensure all requirements are met before task completion') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Standardize processes with reusable checklist templates') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}

.checklist-example {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background: #fafafa;
}

.checklist-example-header {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.checklist-example-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.checklist-example-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.checklist-example-progress-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.checklist-example-progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #2aabee 0%, #229ed9 100%);
  transition: width 0.3s ease;
}

.checklist-example-progress-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
}

.checklist-example-items {
  list-style: none;
  padding: 0;
  margin: 0;
}

.checklist-example-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  margin-bottom: 4px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.checklist-example-item.completed {
  opacity: 0.7;
}

.checklist-example-item.completed .checklist-example-item-text {
  text-decoration: line-through;
  color: #999;
}

.checklist-example-checkbox {
  cursor: default;
  flex-shrink: 0;
  margin-top: 2px;
}

.checklist-example-item-text {
  flex: 1;
  user-select: none;
  color: #333;
}
</style>
